import { useState, type Dispatch, type SetStateAction } from "react";
import { useDataContext } from "../../DataProvider/DataContext";
import { CommonButton } from "../../styledComponents/MUI.styles";
import { SelectField } from "../formFields/SelectField";

import { DotFlashing, FlexColumnDiv, FlexRowDiv } from "../../styledComponents/Components.styles";
import { ECUCataSelectProgramOptionInterface, ecucCataSelectObjTemplate, type ECUCataSelectOptionInterface, type ECUCataSelectOptionVariantInterface } from "../../../models/ECUCInterfaces";
import { addClustersOptionsToMachineToVariantData, createSelectionCriteriaList, fetchProcessedClusterDataForVariant, getMachineDataMap, getModuleDataMap, getProgramDataMap, getVariantDataMap, processMachineSingleItemData, processVariantSingleItemData, reorderClusterListBySwInstallationSequence, sortVehicleProgramsByModelYear, TypeMachineToVariantDataMap, validateClusterDataForVariants, validateVariantDataForMachines } from "../../../utilities/ecuCatalogeUtils";
import type { APIServiceFormat } from "../../../APIs/APIService";
import { srpdParentLevelTemplate, srpTemplate, type SRPDMachineListInterface, type SRPDModuleInformationInterface, type SRPDPreAssemblyImageInterface, type StateClusterListInterface, type StateMachineListInterface, type StateSRPdParentLevelInterface, type StateVariantListInterface } from "../../../models/SRPSchema";
import { IconButton, Tooltip } from "@mui/material";
import palette from "../../../utilities/palette";
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImportSection from "./ImportSection/ImportSection";
import { populateSupplierInVariantList, populateMachineListErrorsSection, getJsonContent, handleFNV23VersioningForMachineList } from "../../../utilities/utilityFunctions";
import { SelectOption } from "../../../utilities/dropDownOptions";
import { ECUCSelectDataProcessError } from "../../../utilities/customErrors";
import { filterProgramOptionsByArchitectureGroup, resolveProgramOptionsWithFallback } from "../../../utilities/programOptionsUtils";
import { UI_SUPPORTED_ARCHITECTURES } from "../../../utilities/constants";




interface ECUCataSelectProps {
    selectedKeyPersonOption: SelectOption; setSelectedKeyPersonOption: Dispatch<SetStateAction<SelectOption>>;
    selectedResponsibleOptions: SelectOption[]; setSelectedResponsibleOptions: Dispatch<SetStateAction<SelectOption[]>>;

    setMachineList: Dispatch<SetStateAction<StateMachineListInterface[]>>;
    setMachineListErrors: Dispatch<SetStateAction<any>>;
    setModuleInformation: Dispatch<SetStateAction<SRPDModuleInformationInterface>>;
    preassemblyImage: SRPDPreAssemblyImageInterface;
    setPreassemblyImage: Dispatch<SetStateAction<SRPDPreAssemblyImageInterface>>;
    textFieldValues: StateSRPdParentLevelInterface;
    setTextFieldValues: Dispatch<SetStateAction<StateSRPdParentLevelInterface>>;
    handleComplete?: () => void; // Optional callback for completion
    moduleInformation: SRPDModuleInformationInterface;
    setProjectKey?: Dispatch<SetStateAction<string>>;
}

export default function ECUCCataSelect ({
    selectedKeyPersonOption, setSelectedKeyPersonOption,
    selectedResponsibleOptions, setSelectedResponsibleOptions,
    setMachineList, 
    setMachineListErrors,
    setModuleInformation, 
    preassemblyImage,
    setPreassemblyImage,
    textFieldValues,
    setTextFieldValues,
    moduleInformation,
    setProjectKey
}: Readonly<ECUCataSelectProps>) {
    
    
    const { 
        api,
        apiService,
        ecuCataSelectObj,
        setECUCataSelectObj,
        formProcessingState, setFormProcessingState,
        setArchitectureType, 
        setECUData, 
        dynamicSelectOptionsMap, setDynamicSelectOptionsMap, 
        importProcessData,
        setImportProcessData,
        tempData,
        setTempData
    } = useDataContext();    

    const [ecuCataLoading, setECUCataLoading] = useState(false);
    const [showPrefill, setShowPrefill] = useState(false);
    // data on manualInputForm layer to be set when ECU Cata data has finished fetching?
        // or set data in the json file directly? (probably this one)

    // selected options in manualInputForm state to persist between tabs?
    const defaultLoadingState = {
        program:false,
        module:false,
        machine:false,
        variant:false,
        cluster:false
    };

    const defaultIsDisabled = {
        program: false,
        module: true,
        machine: true,
        variant: true,
        cluster: true
    };

    const [loadingState, setLoadingState] = useState(structuredClone(defaultLoadingState));

    const [isDisabled, setIsDisabled] = useState(structuredClone(defaultIsDisabled));
    const [error, setError] = useState({
        isError: false,
        message: ""
    });

    const getProgramOptions = async (selectedProgramOptions: ECUCataSelectProgramOptionInterface[]) => {
        try {
            // Init options for each select field
            setError({
                isError: false,
                message: ""
            });
            setLoadingState((prevState) => ({
                ...prevState,
                program: true
            }));
            
            console.log(`Selected Programs`, selectedProgramOptions);
            if (!apiService) {
                throw new Error("APIService instance is not available");
            }

            // Filter program options by electrical architecture when it is available.
            // Fallback to currently available options for legacy/localStorage state.
            const unfilteredProgramOptions = structuredClone(
                resolveProgramOptionsWithFallback(ecuCataSelectObj.programs)
            );

            const filteredOptions = filterProgramOptionsByArchitectureGroup(
                unfilteredProgramOptions,
                selectedProgramOptions
            );

            setECUCataSelectObj((prevState) => ({
                ...prevState,
                programs: {
                    ...prevState.programs,
                    options: filteredOptions,
                    value: selectedProgramOptions
                },
                module: {
                    options: [],
                    value: null // Reset module value
                }
            })); 

            setIsDisabled((prevState) => ({
                ...prevState,
                program: false
            }));

            setLoadingState((prevState) => ({
                ...prevState,
                program: false
            }));
     } catch(error: any) {
        console.error("Error fetching program options:", error);
        setError({
            isError: true,
            message: "An error has occurred while fetching program options. If the problem persists, please reach out to the development team."
        });
        setIsDisabled((prevState) => ({
            ...prevState,
            program: false
        }));
        setLoadingState((prevState) => ({
            ...prevState,
            program: false
        }));
     }
    }

    const getModuleOptions = async (selectedPrograms: ECUCataSelectOptionInterface[]) => {
        setError({
            isError: false,
            message: ""
        });
        setIsDisabled((prevState) => ({
            ...prevState,
            module: true
        }));

        setLoadingState((prevState) => ({
            ...prevState,
            module: true
        }));

        try {

        if (!selectedPrograms || selectedPrograms.length === 0) {
            console.warn("No programs selected, skipping module options fetch");
            setLoadingState((prevState) => ({
                ...prevState,
                module: false
            }));
            return;
        }

        if (!apiService) {
            throw new Error("APIService instance is not available");
        }
        const programIds = selectedPrograms.map((program) => program.value);
        const response = await apiService.getModuleOptions(programIds);

        if (!response.isSuccess) {
            console.error("Failed to fetch module options:", response.message);
            setError({
                isError: true,
                message: "An error has occurred while fetching module options. If the problem persists, please reach out to the development team."
            });
            return;
        };

        const moduleOptions = response.data;


        setECUCataSelectObj((prevState) => ({
            ...prevState,
            module: {
                options: moduleOptions,
                value: null
            }
        }));

        setIsDisabled((prevState) => ({
            ...prevState,
            module: false
        }));

        setLoadingState((prevState) => ({
            ...prevState,
            module: false
        }));

    } catch (error) {
        console.error("Error fetching module options:", error);
        setError({
            isError: true,
            message: "An error has occurred while fetching module options. If the problem persists, please reach out to the development team."
        });
        setIsDisabled((prevState) => ({
            ...prevState,
            module: false
        }));
        setLoadingState((prevState) => ({
            ...prevState,
            module: false
        }));
    }
    }

    const getMachineOptions = async (selectedModuleId: ECUCataSelectOptionInterface) => {

        setError({
            isError: false,
            message: ""
        });
        setIsDisabled((prevState) => ({
            ...prevState,
            machine: true
        }));

        setLoadingState((prevState) => ({
            ...prevState,
            machine: true
        }));

        try {

        let programIds = ecuCataSelectObj.programs.value.map((program) => program.value);
        const moduleId = selectedModuleId?.value;

        if (!apiService) {
            throw new Error("APIService instance is not available");
        }

        const response = await apiService.getMachineOptions(programIds, moduleId);

        if (!response.isSuccess) {
            console.error("Failed to fetch machine options:", response.message);
            setError({
                isError: true,
                message: response.message
            });
            return;
        };

        const machineOptions = response.data;

        setECUCataSelectObj((prevState) => ({
            ...prevState,
            machines: {
                options: machineOptions,
                value: []
            }

        }));

        setIsDisabled((prevState) => ({
            ...prevState,
            machine: false
        }));

        setLoadingState((prevState) => ({
            ...prevState,
            machine: false
        }));
    } catch (error) {
        console.error("Error fetching machine options:", error);
        setError({
            isError: true,
            message: "An error has occurred while fetching machine options. If the problem persists, please reach out to the development team."
        });
        setIsDisabled((prevState) => ({
            ...prevState,
            machine: false
        }));
        setLoadingState((prevState) => ({
            ...prevState,
            machine: false
        }));
    }
    }
    


    const getVariantOptions = async (selectedOptions: ECUCataSelectOptionInterface[]) => {
        setError({
            isError: false,
            message: ""
        });
        setIsDisabled((prevState) => ({
            ...prevState,
            variant: true
        }));

        setLoadingState((prevState) => ({
            ...prevState,
            variant: true
        }));

        try {

        console.log(`Selected Options`, selectedOptions);
        let machineIds = selectedOptions.map((option) => option.value);
        console.log(`Selected Machine IDs`, machineIds);
        let programIds = ecuCataSelectObj.programs.value.map((option => option.value));
        let moduleId = ecuCataSelectObj.module.value?.value;


        if (!apiService || !moduleId) {
            throw new Error("APIService instance is not available");
        }
        const response = await apiService.getVariantOptions(programIds, moduleId, machineIds);

        if (!response.isSuccess) {
            console.error("Failed to fetch variant options:", response.message);
            setError({
                isError: true,
                message: "An error has occurred while fetching variant options. If the problem persists, please reach out to the development team."
            });
            return;
        }
        let variantOptions = response.data;
        

        setECUCataSelectObj((prevState) => ({
            ...prevState,
            variants: {
                options: variantOptions,
                value: []
            }
        }));

        setIsDisabled((prevState) => ({
            ...prevState,
            variant: false
        }));

        setLoadingState((prevState) => ({
            ...prevState,
            variant: false
        }));
    } catch (error) {
        console.error("Error fetching variant options:", error);
        setError({
            isError: true,
            message: "An error has occurred while fetching variant options. If the problem persists, please reach out to the development team."
        });
        setIsDisabled((prevState) => ({
            ...prevState,
            variant: false
        }));
        setLoadingState((prevState) => ({
            ...prevState,
            variant: false
        }));
    }
    }

    async function getClusterOptions(): Promise<{
        label: string;
        value: string;
        variantId: string;
        machineId: string;
    }[]> {
        setError({
            isError: false,
            message: ""
        });

        try {
        const variants = ecuCataSelectObj.variants.value;
        const programIds = ecuCataSelectObj.programs.value.map((option) => option.value);
        const moduleId = ecuCataSelectObj.module.value?.value;
        const machineIds = ecuCataSelectObj.machines.value.map((option) => option.value);

        if (!programIds.length || !moduleId || !machineIds.length || !variants.length) {
            throw new Error("Internal Error: Insufficient data to fetch cluster options");
        }
        if (!apiService) {
            throw new Error("Internal Error: APIService instance is not available");
        }
        const response = await apiService.getClusterOptions(
            programIds,
            moduleId,
            machineIds,
            variants
        );

        if (!response.isSuccess) {
            console.error("Failed to fetch cluster options:", response.message);
            setError({
                isError: true,
                message: "An error has occurred while fetching cluster options. If the problem persists, please reach out to the development team for support."
            });
            return [];
        }

        const clusterOptions = response.data.map((option: { label: string; value: string, variantId: string, machineId: string }) => ({
            label: option.label,
            value: option.value,
            variantId: option.variantId,
            machineId: option.machineId
        }));


        console.log(`Cluster Options`, clusterOptions);

        return clusterOptions;
    } catch (error) {
        console.error("Error fetching cluster options:", error);
        setError({
            isError: true,
            message: "An error has occurred while fetching cluster options. If the problem persists, please reach out to the development team for support."
        });
        return [];
    }
    }

    function filterVariantDataByRequestedVariants(variantDataObj: any) {
        // Workaround: variantData can include more entries than requested.
        // Keep only variants that were explicitly selected in ecuCataSelectObj.variants.value.
        const selectedVariantKeys = new Set(
            ecuCataSelectObj.variants.value.map((variant) => `${String(variant.machineId)}::${String(variant.value)}`)
        );

        for (const machineId of Object.keys(variantDataObj)) {
            const variantEntries = Object.entries(variantDataObj[machineId]?.variants ?? {});
            const filteredVariants = Object.fromEntries(
                variantEntries.filter(([machineVariantId]) => selectedVariantKeys.has(`${String(machineId)}::${String(machineVariantId)}`))
            );

            variantDataObj[machineId] = {
                ...variantDataObj[machineId],
                variants: filteredVariants,
            };
        }

        return variantDataObj;
    }

    function processData({programData, moduleData, machineData, variantData, clusterOptions}: {
        programData: APIServiceFormat,
        moduleData: APIServiceFormat,
        machineData: APIServiceFormat,
        variantData: APIServiceFormat,
        clusterOptions: {
            label: string;
            value: string;
            variantId: string;
            machineId: string;
        }[],
    }){
        try {
            if (!programData.isSuccess || !moduleData.isSuccess || !machineData.isSuccess || !variantData.isSuccess) {
                throw new Error("Failed to fetch data from API");
            }
            
            const machineDataObj = getMachineDataMap(machineData.data);
            const variantDataObj = filterVariantDataByRequestedVariants(getVariantDataMap(variantData.data));


            console.log(`Processed Machine Data Map`, machineDataObj);
            console.log(`Processed Variant Data Map`, variantDataObj);
            console.log(`Cluster Options`, clusterOptions);



            // Validate if variantDataObj has entries for each machineId in machineDataObj
            validateVariantDataForMachines(machineDataObj, variantDataObj);

            // Validate if each variant has at least one cluster in clusterOptions
            validateClusterDataForVariants(variantDataObj, clusterOptions);

            for (let machine in machineData.data){
                const machineId = machineData.data[machine].machineId;

                machineDataObj[machineId]["variants"] = variantDataObj[machineId]["variants"]
            }

            addClustersOptionsToMachineToVariantData(clusterOptions, machineDataObj);


            const data = {
                programData: getProgramDataMap(programData.data),
                moduleData: getModuleDataMap(moduleData.data),
                machineToVariantData: machineDataObj,
            }

            return data;
        } catch (error) {
            console.error("Error processing data:", error);
            throw error;
        }


    }

    const getFormData = async () => 
    {
        try {

            setECUCataLoading(true);
            // how to kick this off sooner since the api calls will be the time gate
            const clusterOptions = getClusterOptions();
            if (!apiService) {
                throw new Error("APIService instance is not available");
            }
            const programIds = ecuCataSelectObj.programs.value.map((program) => program.value);
            const moduleId = ecuCataSelectObj.module.value?.value;
            const moduleName = ecuCataSelectObj.module.value?.label || "";
            const machineIds = ecuCataSelectObj.machines.value.map((machine) => machine.value);
            const variantIds = ecuCataSelectObj.variants.value.map((variant) => variant.value);

            if (!programIds.length || !moduleId || !moduleName || !machineIds.length || !variantIds.length) {
                throw new Error("Internal Error: Insufficient data to fetch form data");
            }

            let programData = apiService.getProgramData(ecuCataSelectObj.programs.value.map((program) => program.value));
            
            let moduleData = apiService.getModuleData(
                programIds,
                moduleId
            );

            let machineData = apiService.getMachineData(
                programIds,
                moduleId,
                machineIds
            );


            let variantData = apiService.getVariantData(
                programIds,
                moduleId,
                machineIds,
                variantIds
            );


            const data = processData({
                programData: await programData,
                moduleData: await moduleData, 
                machineData: await machineData,
                variantData: await variantData,
                clusterOptions: await clusterOptions,

            });

            console.log(`Processed Data`, data);
            await processAndUpdateSRPdStates(data);
            setECUCataLoading(false);

        } catch (error: any) {
            console.error("Error getting form data:", error);
            const errorMessage = error instanceof ECUCSelectDataProcessError ? `Data processing error: ${error.message}` : "An error has occurred while fetching form data. If the problem persists, please reach out to the development team.";
            setError({
                isError: true,
                message: errorMessage
            });
            setECUCataLoading(false);
        }
    }

    async function processAndUpdateSRPdStates(data: {
        programData: {
            vehiclePrograms: string[];
            architectureType: string;
            electricalArchitectures: string[];
            lveas: string[];
        };
        moduleData: {
            moduleName: string;
            moduleDescription: string;
        };
        machineToVariantData: TypeMachineToVariantDataMap;
    }) {
        const getCSSMismatchErrorMessage = (clusterName: string, cssPjKey: string, moduleName: string) =>
            `Extracted CSS from cluster name ${clusterName} does not match the registered CSS ${cssPjKey} for module ${moduleName}. Please make sure the cluster names in the ECU Catalog follow the naming convention and include the correct CSS as a prefix. If you believe this is an error, please reach out to the development team for support.`;

        const newDynamicSelectOptionsMap = structuredClone(dynamicSelectOptionsMap);

        // Process signing directory options
        const signingIdsOptions = await populateSigningIdOptions(data);

        newDynamicSelectOptionsMap.signingIdsOptions = signingIdsOptions;

        // ArchitectureType
        // get from programData first key
        let architectureType = data.programData.architectureType;

        if (!UI_SUPPORTED_ARCHITECTURES.includes(architectureType)) {
            throw new Error(`Unsupported architecture type: ${architectureType}`);
        }

        setArchitectureType(architectureType);

        // ModuleInformation
        let newModuleInformation = structuredClone(moduleInformation);
        newModuleInformation = {
            ...newModuleInformation,
            // replace space with underscore
            moduleName: data.moduleData.moduleName.replace(/ /g, "_"),
            lveas: data.programData.lveas,
            vehiclePrograms: data.programData.vehiclePrograms,
        }

        let vehicleProgramList = Array.from(new Set([
            ...newDynamicSelectOptionsMap.vehiclePrograms.map((option) => option.value),
            ...newModuleInformation.vehiclePrograms
        ])); 
        vehicleProgramList = vehicleProgramList.filter((vehicleProgram) => vehicleProgram && vehicleProgram !== "");
        vehicleProgramList = vehicleProgramList.sort((a, b) => a.localeCompare(b));

        newDynamicSelectOptionsMap.vehiclePrograms = vehicleProgramList.map((vehicleProgram) => (
            {
                label: vehicleProgram,
                value: vehicleProgram
            } 
        ));

        let lveaList = Array.from(new Set([
            ...newDynamicSelectOptionsMap.lveas.map((option) => option.value),
            ...newModuleInformation.lveas
        ])); 
        lveaList = lveaList.filter((lvea) => lvea && lvea !== "");
        lveaList = lveaList.sort((a, b) => a.localeCompare(b));

        console.log(`LVEA List`, lveaList);
        newDynamicSelectOptionsMap.lveas = lveaList.map((lvea) => 
            ({
                label: lvea,
                value: lvea
            }));

        const machineNameList = [] as string[];
        const selectionCriteriaList = createSelectionCriteriaList(newModuleInformation.vehiclePrograms);

        const cssPjKey = await fetchCSSPjKey(data.machineToVariantData);
        setProjectKey(cssPjKey);

        // MachineList
        let newMachineList = [] as StateMachineListInterface[];
            for ( let machineId in data.machineToVariantData){
                const machine = data.machineToVariantData[machineId]
                let newMachine = {
                    ...structuredClone(srpTemplate.machineList[0]), 
                    ...processMachineSingleItemData(machine),
                    variantList: [] as StateVariantListInterface[]
                };
                machineNameList.push(newMachine.machineName);

                // Populate variantList, add variant if machineId matches
                for (let variantId in machine["variants"]){
                    const variant = machine["variants"][variantId];

                    let newVariant = structuredClone(srpTemplate.machineList[0].variantList[0]);
                    newVariant = {...newVariant, ...processVariantSingleItemData(variant)};

                    //blank out cluster list
                    newVariant.clusterList = [] as StateClusterListInterface[];

                    const processedClusterDataList = await fetchProcessedClusterDataForVariant({
                        machineId: String(machineId),
                        variantId: String(variantId),
                        variant,
                        ecuCataSelectObj,
                        selectionCriteriaList,
                        apiService,
                    });

                    for (const clusterData of processedClusterDataList) {
                        
                        const clusterPjKey = clusterData.clusterName?.split("_")[0];

                            if (clusterPjKey && clusterPjKey !== cssPjKey) {
                                throw new ECUCSelectDataProcessError(getCSSMismatchErrorMessage(clusterData.clusterName, cssPjKey, newModuleInformation.moduleName));
                            }

                            let newCluster = structuredClone(srpTemplate.machineList[0].variantList[0].clusterList[0]);
                            newCluster = {...newCluster, ...clusterData};
                            newVariant.clusterList.push(newCluster);
                    }
                    newMachine.variantList.push(newVariant);
                }
                newMachineList.push(newMachine);
        }

        reorderClusterListBySwInstallationSequence({ machineList: newMachineList });
        if (["FNV2", "FNV3"].includes(architectureType)) {
            await handleFNV23VersioningForMachineList({
                machineList: newMachineList,
                api: api,
            })
        }

        setDynamicSelectOptionsMap(newDynamicSelectOptionsMap);
        setECUData((prevState) => ({
            ...prevState,
            programData: data.programData,
            moduleData: data.moduleData,
            machineToVariantData: data.machineToVariantData
        }));
        
        // Create updated text field values
        const moduleName = newModuleInformation.moduleName;
        const srpName = `${cssPjKey}_${moduleName}`.replace(/ /g, "_");
        const newTextFieldValues = {
            ...textFieldValues,
            srpdName: srpName
        };

        setTempData((prevState: any) => ({
            ...prevState,
            textFieldValues: newTextFieldValues,
            moduleInformation: newModuleInformation,
            machineList: newMachineList,
        }));

        

        if (importProcessData.enabled) {
            const { 
                importedTextFieldValues,
                importedSelectedKeyPersonOption,
                importedSelectedResponsibleOptions,
                importedModuleInformation,
                importedMachineList,
                importedPreassemblyImage 
            } = await processImportData({
                    processedTextFieldValues: newTextFieldValues,
                    processedMachineList: newMachineList,
                    electricalArchitectures: data.programData.electricalArchitectures,
                });

            await handlePostProcessing(
                importedTextFieldValues,
                importedSelectedKeyPersonOption,
                importedSelectedResponsibleOptions,
                importedModuleInformation,
                importedMachineList,
                importedPreassemblyImage,
            );
            return; 
        }

        await handlePostProcessing(
            newTextFieldValues,
            null,
            [],
            newModuleInformation,
            newMachineList,
            structuredClone(srpTemplate.preassemblyImage),
        );
    }

    async function populateSigningIdOptions(data: { programData: any; moduleData: any; machineToVariantData: any; }) {
        const signingIdRequestList = [] as {
            modelYearProgramCodes: string;
            moduleName: string;
            machineName: string;
            canDiagRxId: string;
            variantName: string;
        }[];
        for (let machineId in data.machineToVariantData) {
            const currentMachine = data.machineToVariantData[machineId];
            const machineName = currentMachine.machineName;
            const canDiagRxId = currentMachine.canDiagRxId;
            const currentVariants = currentMachine.variants;


            for (let variantId in currentVariants) {
                const currentVariant = currentVariants[variantId];
                const variantName = currentVariant.variantName;
                const modelYearProgramCodes = data.programData.vehiclePrograms.join(",");
                signingIdRequestList.push({
                    modelYearProgramCodes,
                    moduleName: data.moduleData.moduleName,
                    machineName,
                    canDiagRxId,
                    variantName
                });

            }
        }

        const signingIdsOptions = [] as {
            value: string;
            label: string;
            machineName: string;
            canDiagRxId: string;
            variantName: string;
        }[];
        const signingIdsOptionsResponseList = await Promise.all(signingIdRequestList.map((requestItem) => apiService.getSigningDirectoryOptions(requestItem)));

        signingIdsOptionsResponseList.forEach((response, index) => {
            const variantId = signingIdRequestList[index].variantName; // Assuming variantName is unique and can be used as an identifier
            if (!response.isSuccess) {
                throw new Error(`Failed to fetch signing directory options for variant ${variantId}: ${response.message}`);
            }
            const data = response.data as string[];
            data.forEach((option) => {
                signingIdsOptions.push({
                    value: option,
                    label: option,
                    machineName: signingIdRequestList[index].machineName,
                    canDiagRxId: signingIdRequestList[index].canDiagRxId,
                    variantName: signingIdRequestList[index].variantName
                });
            });
        });
        console.log(`Populated Signing IDs Options:`, signingIdsOptions);
        return signingIdsOptions;
    }

    async function processImportData({
        processedTextFieldValues,
        processedMachineList,
        electricalArchitectures,
    }: {
        processedTextFieldValues: StateSRPdParentLevelInterface;
        processedMachineList: StateMachineListInterface[];
        electricalArchitectures: string[];
    }) {
        const importedTextFieldValues = structuredClone(srpdParentLevelTemplate);
        importedTextFieldValues.srpdName = processedTextFieldValues.srpdName;
        importedTextFieldValues.srpdVersion = importProcessData?.data.srpdVersion || "";
        importedTextFieldValues.srpAssignee = importProcessData?.data.srpAssignee || "";
        importedTextFieldValues.electricalArchitecture = electricalArchitectures;
        importedTextFieldValues.srpSchemaVersion = importProcessData?.data.srpSchemaVersion || "";
        importedTextFieldValues.srpWatchers = importProcessData?.data.srpWatchers || [];
        importedTextFieldValues.releaseObjectKey = importProcessData?.data.releaseObjectKey || "";
        importedTextFieldValues.fixVersion = importProcessData?.data.fixVersion || "";

        const importedSelectedKeyPersonOption = importProcessData?.data.srpAssignee ? {
            label: importProcessData.data.srpAssignee,
            value: importProcessData.data.srpAssignee,
        } : null;

        const importedSelectedResponsibleOptions = importProcessData?.data.srpWatchers ? importProcessData.data.srpWatchers.map((watcher: string) => ({
            label: watcher,
            value: watcher,
        })) : [];

        const importedModuleInformation = importProcessData?.data.moduleInformation || structuredClone(srpTemplate.moduleInformation);
        if (Array.isArray(importedModuleInformation.vehiclePrograms)) {
            importedModuleInformation.vehiclePrograms = sortVehicleProgramsByModelYear(importedModuleInformation.vehiclePrograms);
        }
        const importedPreassemblyImage = importProcessData?.data.preassemblyImage || structuredClone(srpTemplate.preassemblyImage);
        importedPreassemblyImage.clusterList = [];

        // Populate non-schema compliant fields in for imported items
        populateNonSchemaCompliantFieldsForImport(processedMachineList);
        // Populate missing clusters in machineList from importProcessData
        populateMissingClustersFromImportedData(processedMachineList);        
        
        let importedMachineList = importProcessData?.data.machineList || structuredClone(srpTemplate.machineList);

        return {
            importedTextFieldValues,
            importedSelectedKeyPersonOption,
            importedSelectedResponsibleOptions,
            importedModuleInformation,
            importedMachineList,
            importedPreassemblyImage
        }

    }

    function populateNonSchemaCompliantFieldsForImport(processedMachineList: StateMachineListInterface[]) {
        for (const machine of processedMachineList) {
            const importedMachine = importProcessData.data.machineList.find((m: StateMachineListInterface) => m.machineName === machine.machineName);
            if (!importedMachine) {
                continue;
            }

            for (const variant of machine.variantList) {
                const importedVariant = importedMachine.variantList.find((v: StateVariantListInterface) => v.variantName === variant.variantName);
                if (!importedVariant) {
                    continue;
                }

                for (const cluster of variant.clusterList) {
                    const importedCluster = importedVariant.clusterList.find((c: StateClusterListInterface) => c.clusterName === cluster.clusterName);
                    if (!importedCluster) {
                        continue;
                    }
                    
                }
            }
        }
    }

    function populateMissingClustersFromImportedData(processedMachineList: StateMachineListInterface[]) {
        for (const machine of processedMachineList) {
            const importedMachine = importProcessData.data.machineList.find((m: StateMachineListInterface) => m.machineName === machine.machineName);
            if (!importedMachine) {
                continue;
            }

            // Check for missing clusters in each variant
            for (const variant of machine.variantList) {
                const importedVariant = importedMachine.variantList.find((v: StateVariantListInterface) => v.variantName === variant.variantName);
                if (!importedVariant) {
                    continue;
                }     
                // Add missing clusters to the imported variant
                for (const cluster of variant.clusterList) {
                    const clusterExists = importedVariant.clusterList.some((c: StateClusterListInterface) => c.clusterName === cluster.clusterName);
                    if (!clusterExists) {
                        importedVariant.clusterList.push(cluster);
                    }
                }
            }
        }
        reorderClusterListBySwInstallationSequence({ machineList: importProcessData.data.machineList });
    }

    async function fetchCSSPjKey(machineToVariantData: TypeMachineToVariantDataMap): Promise<string> {
        const machineIds = Object.keys(machineToVariantData);
        if (machineIds.length === 0) {
            throw new Error("No machine data available to fetch CSS PJ Key");
        }
        const firstMachineId = machineIds[0];
        return await apiService.getCss(firstMachineId);
    }

    async function handlePostProcessing(
        textFieldValues: StateSRPdParentLevelInterface,
        selectedKeyPersonOption: SelectOption | null,
        selectedResponsibleOptions: SelectOption[],
        moduleInformation: SRPDModuleInformationInterface,
        machineList: SRPDMachineListInterface[],
        preassemblyImage: SRPDPreAssemblyImageInterface,

    ) {
        preassemblyImage.clusterList = [];
        setTextFieldValues(textFieldValues);
        setSelectedKeyPersonOption(selectedKeyPersonOption);
        setSelectedResponsibleOptions(selectedResponsibleOptions);
        setModuleInformation(moduleInformation);
        setMachineList(machineList);
        populateSupplierInVariantList(machineList);
        populateMachineListErrorsSection(machineList, setMachineListErrors);
        setPreassemblyImage(preassemblyImage);


        const prefilledJSONContent = getJsonContent({
            textFieldValues: tempData.current.textFieldValues,
            selectedKeyPersonOption: null,
            selectedResponsibleOptions: [],
            moduleInformation: tempData.current.moduleInformation,
            machineList: tempData.current.machineList,
            preassemblyImage: structuredClone(srpTemplate.preassemblyImage),
        });
        prefilledJSONContent.preassemblyImage.clusterList = [];
        console.log(`prefilledJSONContent:`, prefilledJSONContent);
        
        const prefilledJSONContentBackup = structuredClone(prefilledJSONContent);
        setECUData((data) => ({
            ...data,
            prefilledJSONContent: prefilledJSONContent,
            prefilledJSONContentBackup: prefilledJSONContentBackup
        }));

        setFormProcessingState("FORM_EDIT");
    }



    console.log(`Import Process Data`, importProcessData);

    return(
    <>
    
    {/* <div> */}
    <FlexRowDiv style={{justifyContent: 'space-between'}}>
        <h2 style={{fontFamily: 'FordF-1-Semibold', fontSize: '30px', marginLeft: '2vw', color: palette.main}}>Release Details</h2>
        <FlexRowDiv>
            <span style={{marginRight: '2vw', marginTop: '2vh'}}>
                <Tooltip title="Import SRPd">
                    <IconButton
                    onClick={() => setShowPrefill(!showPrefill)}
                    >
                    <UploadFileIcon
                        sx={{
                        color: palette.main,
                        fontSize: '36px',
                        ":hover": {
                        color: palette.hover
                        }}}                        />
                    </IconButton>
                    
                </Tooltip>
            </span>
            
        </FlexRowDiv>


    </FlexRowDiv>


       { !importProcessData.enabled && <p style={{fontFamily: 'FordF-1-Regular', fontSize: '16px', marginLeft: 'calc(2.5vw + 5px)', marginTop: '0px'}}>
            Please make your selections
        </p>}

    {/* </div> */}


    <div
        style={{
            display: 'flex',
            flexDirection: 'column',
            fontSize: '16px',
            minHeight: '400px',
            justifyContent: 'space-evenly',
            flex: '1 1 0',
            marginLeft: '2.5vw'
        }}
    >
        <div style={{display: 'flex', flexDirection: 'column', gap: '5px', flex: '2 1'}}>
            <SelectField
                label={"Program"}
                isMulti={true}
                idInput={"program-input"}
                nameInput={"program"}
                options={ecuCataSelectObj.programs.options}
                isClearable={true}
                value={ecuCataSelectObj.programs.value}
                isLoading={loadingState.program}
                isDisabled={isDisabled.program || importProcessData.enabled}
                placeholder={"--Please Select--"}
                onChange={async (selectedOption: SelectOption[]) => {
                    console.log(`Selected Programs`, selectedOption);

                    setECUCataSelectObj((prevState) => ({
                        ...prevState,
                        programs: {
                            ...prevState.programs,
                            value: selectedOption
                        },
                        module: {
                            options: [],
                            value: null
                        },
                        machines: {
                            options: [],
                            value: []
                        },
                        variants: {
                            options: [],
                            value: []
                        },
                        clusters: {
                            options: [],
                            value: []
                        }
                    }));

                    setIsDisabled((prevState) => ({ 
                        ...prevState,   
                        module: true,
                        machine: true,
                        variant: true,
                        cluster: true
                    })
                    )


                    await getProgramOptions(selectedOption);
                    if (selectedOption.length !== 0) {
                        await getModuleOptions(selectedOption);
                    }
                }}
            />
                
            <SelectField
                label={"Module"}
                idInput={"module-input"}
                nameInput={"module"}
                options={ecuCataSelectObj.module.options}
                value={ecuCataSelectObj.module.value}
                isLoading={(loadingState.module)}
                isDisabled={isDisabled.module || importProcessData.enabled}
                isClearable={true}
                placeholder={"--Please Select--"}
                onChange={async (selectedOption: SelectOption) => {
                    setECUCataSelectObj((prevState) => ({
                        ...prevState,
                        module: {
                            options: prevState.module.options,
                            value: selectedOption
                        },
                        machines: {
                            options: [],
                            value: [],
                        },
                        variants: {
                            options: [],
                            value: []
                        },
                        clusters: {
                            options: [],
                            value: []
                        }
                    }));

                    setIsDisabled((prevState) => ({
                        ...prevState,
                        machine: true,
                        variant: true,
                        cluster: true
                    }));

                    if (selectedOption) {
                        await getMachineOptions(selectedOption);
                    }
                }}
            />

            <SelectField
                label={"Machines"}
                isMulti
                idInput={"machine-input"}
                isClearable
                options={[{
                    label: "Select All",
                    value: "select_all"
                },...ecuCataSelectObj.machines.options]}
                value={ecuCataSelectObj.machines.value}
                isLoading={loadingState.machine}
                isDisabled={isDisabled.machine || importProcessData.enabled}
                placeholder={"--Please Select--"}
                onChange={async (selectedOption: SelectOption[]) => {
                    const isSelectAllSelected = selectedOption.some((option) => option.value === "select_all");
                    const selectedMachines = isSelectAllSelected
                        ? structuredClone(ecuCataSelectObj.machines.options)
                        : selectedOption;

                    setECUCataSelectObj((prevState) => ({
                        ...prevState,
                        machines: {
                            options: prevState.machines.options,
                            value: selectedMachines
                        },
                        variants: {
                            options: [],
                            value: [],
                        },
                        clusters: {
                            options: [],
                            value: []
                        }
                    }));


                    setIsDisabled((prevState) => ({
                        ...prevState,
                        variant: true,
                        cluster: true
                    }));

                    if (selectedMachines.length !== 0) {
                        await getVariantOptions(selectedMachines);
                    }
                }
                }
            />

            <SelectField
                label={"Variants"}
                isMulti
                idInput={"variant-input"}
                isClearable
                options={[{
                    label: "Select All",
                    value: "select_all",
                    machineId: ""
                }, ...ecuCataSelectObj.variants.options]}
                value={ecuCataSelectObj.variants.value}
                isLoading={loadingState.variant}
                isDisabled={isDisabled.variant || importProcessData.enabled}
                placeholder={"--Please Select--"}
                onChange={(selectedOption: ECUCataSelectOptionVariantInterface[]) => {
                    const isSelectAllSelected = selectedOption.some((option) => option.value === "select_all");
                    const selectedVariants = isSelectAllSelected
                        ? structuredClone(ecuCataSelectObj.variants.options)
                        : selectedOption;

                    setECUCataSelectObj((prevState) => ({
                        ...prevState,
                        variants: {
                            options: prevState.variants.options,
                            value: selectedVariants
                        },
                        clusters: {
                            options: [],
                            value: []
                        }
                    }));
                }}
            />        
        </div>

        {importProcessData.enabled && 
            <FlexRowDiv>

                <FlexColumnDiv style={{marginTop: '10px', fontFamily: 'FordF-1-Regular', fontSize: '16px'}}>
                    <p>Form is in <span style={{fontWeight: 700, color: palette.main}}> Import Mode</span>. Program, Module, Machine, and Variant options are disabled.</p>
                    
                    {
                        importProcessData?.failedMetadataErrorMessages?.length === 0 && importProcessData?.registeredDataErrorMessages?.length === 0 ? 
                        <>
                            <p>To reset the form, <button type="button" onClick={handleResetForm} style={{cursor: 'pointer', color: 'blue', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, font: 'inherit'}}>click here</button>.</p>
                            {importProcessData.unRegisteredClusters.length > 0 &&
                                <FlexColumnDiv style={{marginTop: '10px'}}>
                                    <span style={{fontWeight: 700, color: palette.warning}}>The following clusters from the imported file were not registered and will not be added to the SRPd:</span>
                                    {importProcessData.unRegisteredClusters.map((cluster) => (
                                        <span key={`unRegisteredClusters-${cluster.clusterName}-${cluster.variantName}`} style={{color: palette.warning, marginTop: '5px'}}>- {cluster.clusterName} under variant {cluster.variantName}</span>
                                    ))}
                                </FlexColumnDiv>
                            }
                        </>
                    :   
                        <>
                            {importProcessData?.registeredDataErrorMessages.length > 0 &&
                                <FlexColumnDiv style={{marginTop: '10px'}}>

                                    <span style={{fontWeight: 700, color: palette.stepper.error}}>Release data not registered</span>
                                    {importProcessData?.registeredDataErrorMessages.map((message) => (
                                        <span key={`registeredDataErrorMessages-${message}`} style={{color: palette.stepper.error, marginTop: '5px'}}>- {message}</span>
                                    ))}

                                </FlexColumnDiv>
                            }
                            {importProcessData?.failedMetadataErrorMessages.length > 0 &&
                                <FlexColumnDiv style={{marginTop: '10px'}}>
                                    <span style={{fontWeight: 700, color: palette.stepper.error}}>Metadata in imported SRP not registered</span>
                                    {importProcessData?.failedMetadataErrorMessages.map((message) => (
                                        <span key={`failedMetadataErrorMessages-${message}`} style={{color: palette.stepper.error, marginTop: '5px'}}>- {message}</span>
                                    ))}

                                </FlexColumnDiv>
                            }
                        </>
                    
                }
                </FlexColumnDiv >
                    
            </FlexRowDiv>
        }

        <div style={{
            marginTop: "30px",
            marginBottom: "30px", 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '10px', 
            flex: '1 1',
            alignItems: 'center',
            justifyContent: 'center',
            }}>    
            {error.isError &&
                <div style={{display: 'flex', flexDirection: 'column', color: palette.stepper.error, paddingLeft: '5px', alignItems:'center', marginTop: '20px'}}>{error.message}</div>}        

            <CommonButton
                onClick={async() => {
                    setError({
                        isError: false,
                        message: ""
                    });
                    if (importProcessData.enabled) {
                        if (importProcessData.failedMetadataErrorMessages.length > 0  || importProcessData.registeredDataErrorMessages.length > 0) {
                            handleResetForm();
                            return;
                        }
                    }
                    await getFormData();
                }}
                disabled={
                    shouldFetchSRPContentBeDisabled()
                }
                sx={{
                    width: '210px',
                }}
            >
                {

                ecuCataLoading ?
                    <>
                        {"Loading"}
                        <span style={{paddingLeft: '5px'}}><DotFlashing size="small"/></span>
                    </>
                : 
                    getFetchButtonText()
                    }   
            </CommonButton>
        </div> 
    </div>

    <ImportSection showModal={showPrefill} setShowModal={setShowPrefill} setError={setError}/>
    </>);

    function getFetchButtonText() {
        if (!importProcessData.enabled) {
            return "Fetch SRP Content";
        }

        if (importProcessData?.failedMetadataErrorMessages?.length > 0 || importProcessData?.registeredDataErrorMessages?.length > 0) {
            return "Reset Form";
        }

        return "Import SRPd Content";
    }

    function handleResetForm() {
        setECUCataSelectObj((prev) => ({
            ...structuredClone(ecucCataSelectObjTemplate),
            programs: {
                ...prev.programs,
                value: []
            }
        }));
        setImportProcessData((prev) => ({ ...prev, enabled: false }));
    }

    function shouldFetchSRPContentBeDisabled(): boolean {
        if (importProcessData.enabled) {
            return false;
        }
        return (ecuCataSelectObj.variants.value.length === 0 || ecuCataLoading || formProcessingState === "FORM_EDIT");
    }
}
